import type { RouterConfig } from '@nuxt/schema';
import { START_LOCATION, type RouteLocationNormalized } from 'vue-router';
import {
  SCROLL_CONTAINER_SELECTOR,
  historyKey,
  planScroll,
  resetScrollContainers,
  restoreScrollContainers,
  scrollMemory,
} from '~/utils/scrollContainers';

const PAGE_READY_FALLBACK_MS = 1200;

type ScrollResult = { left?: number; top?: number; el?: string; behavior?: ScrollBehavior } | false;

function hashMarginTop(selector: string): number {
  try {
    const el = document.querySelector(selector);
    if (!el) return 0;
    return (
      (Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0) +
      (Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0)
    );
  } catch {
    return 0;
  }
}

function scrollToHash(hash: string, behavior: ScrollBehavior): ScrollResult {
  let el: Element | null = null;
  try {
    el = document.querySelector(hash);
  } catch {
    return false;
  }
  // Anchors inside a scrolling <main> are out of reach of vue-router's window scroll.
  if (el?.closest(SCROLL_CONTAINER_SELECTOR)) {
    el.scrollIntoView({ block: 'start', behavior });
    return false;
  }
  return { el: hash, top: hashMarginTop(hash), behavior };
}

function applyPlan(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  savedPosition: { left: number; top: number } | null,
): ScrollResult {
  switch (planScroll(to, from, !!savedPosition)) {
    case 'keep':
      return false;
    case 'hash':
      return scrollToHash(to.hash, 'auto');
    case 'restore':
      restoreScrollContainers(scrollMemory.get(historyKey()));
      return savedPosition ?? false;
    case 'top':
      resetScrollContainers();
      return { left: 0, top: 0 };
  }
}

export default <RouterConfig>{
  scrollBehavior(to, from, savedPosition) {
    const sameRoute = to.path.replace(/\/$/, '') === from.path.replace(/\/$/, '');
    if (sameRoute || from === START_LOCATION) return applyPlan(to, from, savedPosition);

    const allowTop = typeof to.meta.scrollToTop === 'function' ? to.meta.scrollToTop(to, from) : to.meta.scrollToTop;
    if (allowTop === false) return false;

    // Scroll only after the leaving page's fade, or the reset is undone when it unmounts.
    const nuxtApp = useNuxtApp();
    const router = useRouter();
    return new Promise((resolve) => {
      let settled = false;
      // A newer navigation owns the scroll; a late resolve must not move its page.
      const done = () =>
        requestAnimationFrame(() => {
          if (settled) return;
          settled = true;
          resolve(router.currentRoute.value.fullPath === to.fullPath ? applyPlan(to, from, savedPosition) : false);
        });
      const unhook = nuxtApp.hooks.hookOnce('page:loading:end', () => {
        const transition = (nuxtApp as unknown as { '~transitionPromise'?: Promise<void> })['~transitionPromise'];
        if (transition) transition.then(done);
        else done();
      });
      // The hook can fire before this navigation listens (cached or synchronous pages); never wait forever.
      setTimeout(() => {
        unhook();
        done();
      }, PAGE_READY_FALLBACK_MS);
    });
  },
};
